import {
  COMPLETE_DIGILOCKER_KYC,
  GET_KYC_LANDING_DATA,
  INITIALIZE_DIGILOCKER_KYC,
} from '../appConstants/APIEndpoint';
import { requestApiGet, requestApiPost } from './index';
import {
  AADHAAR_DOC_TYPE,
  OTP_STRATEGY,
  type IKycCompleteDigiLockerData,
  type IKycDocIdData,
  type IKycInitDigiLockerData,
  type IKycLandingData,
} from '../interfaces/Kyc';

/**
 * DigiLocker KYC.
 *
 * Android runs this through the native SurePass DigiBoost SDK, but the SDK is a
 * wrapper around a webview and its result never reaches the backend — only
 * `clientId` does. So on web the same flow is: init → send the browser to the
 * returned `url` → complete with `clientId`. No vendor SDK involved.
 */

/**
 * Resolves the Aadhaar document type and whether DigiLocker is the strategy.
 *
 * Best-effort: the landing payload is large and unverified here, so a failure
 * or an unexpected shape falls back to Aadhaar/DigiLocker rather than blocking
 * verification. Mirrors `isDigiLockerModeActive` — only an explicit "OTP"
 * strategy opts out.
 */
export async function fetchKycDocIdData(): Promise<IKycDocIdData> {
  try {
    const res = await requestApiGet<IKycLandingData>(GET_KYC_LANDING_DATA);
    const docIdData =
      res?.response?.data?.aadharData?.docNumberFlow?.docIdData ?? {};

    return {
      documentType: docIdData.documentType ?? AADHAAR_DOC_TYPE,
      verificationStrategy: docIdData.verificationStrategy,
    };
  } catch {
    return { documentType: AADHAAR_DOC_TYPE };
  }
}

export function isDigiLockerStrategy(strategy?: string): boolean {
  return (strategy || '').toUpperCase() !== OTP_STRATEGY;
}

export async function initializeDigiLockerKyc(
  docType: number,
): Promise<IKycInitDigiLockerData> {
  const res = await requestApiPost<IKycInitDigiLockerData>(
    INITIALIZE_DIGILOCKER_KYC,
    { docType },
  );
  const response = res?.response;

  if (!response?.success) {
    throw new Error(
      response?.error || response?.message || 'Could not start verification',
    );
  }
  return response?.data ?? {};
}

export async function completeDigiLockerKyc(
  clientId: string,
  docType: number,
): Promise<IKycCompleteDigiLockerData> {
  const res = await requestApiPost<IKycCompleteDigiLockerData>(
    COMPLETE_DIGILOCKER_KYC,
    { clientId, docType },
  );
  const response = res?.response;

  if (!response?.success) {
    throw new Error(response?.error || response?.message || 'Could not confirm verification',
    );
  }
  return response?.data ?? {};
}
